import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import HeroMockLanes from '../components/HeroMockLanes';
import DemoVideoModal from '../components/DemoVideoModal';
import Logo from '../components/Logo';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../components/ui/navigation-menu';
import Footer from '../components/Footer';
import { APP_DASHBOARD_URL, APP_ORIGIN } from '../lib/appUrl';
import { 
  ArrowRight, 
  Zap, 
  AlertCircle, 
  XCircle, 
  Target,
  TrendingUp,
  Shield,
  Filter,
  Bell,
  PlayCircle
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [annualBilling, setAnnualBilling] = useState(true);

  const pricingPlans = [
    {
      id: 'mini',
      name: 'Mini',
      description: 'For solo founders shipping their first product',
      monthly: 9,
      annual: 7,
      annualTotal: 84,
      features: [
        '80 AI analyses / month',
        'Unlimited workspaces',
        'Build/Fix/Ignore lanes + ROI',
        'Quick wins insights',
      ],
      cta: 'Start with Mini',
      variant: 'outline',
      highlight: false,
    },
    {
      id: 'basic',
      name: 'Basic',
      description: 'For indie hackers running multiple products',
      monthly: 24,
      annual: 19,
      annualTotal: 228,
      features: [
        '250 AI analyses / month',
        'Everything in Mini',
        'Priority AI categorization',
        'Priority email support',
      ],
      cta: 'Start with Basic',
      variant: 'default',
      highlight: true,
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'For agencies and serious operators',
      monthly: 59,
      annual: 47,
      annualTotal: 564,
      features: [
        '600 AI analyses / month',
        'Everything in Basic',
        'Advanced revenue forecasting',
        'Priority 24h SLA support',
      ],
      cta: 'Start with Pro',
      variant: 'outline',
      highlight: false,
    },
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav 
        className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        data-testid="nav"
      >
        <div className="container">
          <div className="flex h-16 items-center justify-between gap-4">
            <a 
              href="/" 
              className="flex items-center gap-2 flex-shrink-0"
              data-testid="nav-logo-link"
            >
              <Logo size="md" />
              <Badge 
                variant="outline" 
                className="text-[10px] px-1.5 py-0 border-orange-500/50 text-orange-600 uppercase tracking-wider font-semibold"
                data-testid="nav-alpha-badge"
              >
                Alpha
              </Badge>
            </a>

            {/* Center nav menus */}
            <NavigationMenu className="hidden lg:flex">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger data-testid="nav-product-trigger">Product</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid grid-cols-1 gap-1 p-4 w-[260px]">
                      <NavigationMenuLink asChild>
                        <a href="/features/ai-prioritization" className="rounded-md p-3 hover:bg-muted block" data-testid="nav-product-ai">
                          <div className="font-medium text-sm">AI Prioritization</div>
                          <div className="text-xs text-muted-foreground">Build / Fix / Ignore with $ math</div>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a href="/features/roadmap" className="rounded-md p-3 hover:bg-muted block" data-testid="nav-product-roadmap">
                          <div className="font-medium text-sm">Roadmap</div>
                          <div className="text-xs text-muted-foreground">Auto-synced from decisions</div>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a href="/features/changelog" className="rounded-md p-3 hover:bg-muted block" data-testid="nav-product-changelog">
                          <div className="font-medium text-sm">Changelog</div>
                          <div className="text-xs text-muted-foreground">Public feed + RSS</div>
                        </a>
                      </NavigationMenuLink>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger data-testid="nav-resources-trigger">Resources</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid grid-cols-1 gap-1 p-4 w-[260px]">
                      <NavigationMenuLink asChild>
                        <a href="/roadmap" className="rounded-md p-3 hover:bg-muted block" data-testid="nav-resource-roadmap">
                          <div className="font-medium text-sm">Public Roadmap</div>
                          <div className="text-xs text-muted-foreground">See what we're shipping next</div>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a href="/changelog" className="rounded-md p-3 hover:bg-muted block" data-testid="nav-resource-changelog">
                          <div className="font-medium text-sm">Changelog</div>
                          <div className="text-xs text-muted-foreground">Every release, with receipts</div>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a href="/faq" className="rounded-md p-3 hover:bg-muted block" data-testid="nav-resource-faq">
                          <div className="font-medium text-sm">FAQ</div>
                          <div className="text-xs text-muted-foreground">Common questions, answered</div>
                        </a>
                      </NavigationMenuLink>
                      <NavigationMenuLink asChild>
                        <a
                          href="/playbook"
                          className="rounded-md p-3 hover:bg-muted block flex items-start justify-between gap-2"
                          data-testid="nav-resource-playbook"
                        >
                          <div>
                            <div className="font-medium text-sm">Founder&apos;s Playbook</div>
                            <div className="text-xs text-muted-foreground">Ship-smart tactics & teardowns</div>
                          </div>
                          <Badge className="text-[10px] px-1.5 py-0 mt-0.5 flex-shrink-0 bg-primary/15 text-primary border-transparent hover:bg-primary/20">New</Badge>
                        </a>
                      </NavigationMenuLink>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <a 
                    href="/pricing" 
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="nav-pricing-link"
                  >
                    Pricing
                  </a>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <a 
                href="/pricing" 
                className="lg:hidden text-sm text-muted-foreground hover:text-foreground"
              >
                Pricing
              </a>
              <Button 
                asChild
                className="btn-cta"
                data-testid="nav-primary-cta-button"
              >
                <a href={APP_DASHBOARD_URL}>Try free</a>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="section relative overflow-hidden" data-testid="hero-section">
        {/* Background gradient accent */}
        <div className="hero-gradient" />
        
        <div className="container relative z-10">
          <motion.div 
            className="mx-auto max-w-4xl text-center"
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.5 }}
          >
            <h1 className="heading-xl text-balance mb-4">
              Know Exactly What to Build to Hit $10k MRR
            </h1>
            
            <p className="text-xl md:text-2xl font-semibold text-foreground mb-8 mx-auto max-w-2xl" style={{letterSpacing: '0.01em'}}>
              Build revenue, fix churn, ignore noise.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-4 mb-4">
              <Button 
                asChild
                size="lg"
                className="btn-cta text-base"
                data-testid="hero-primary-cta-button"
              >
                <a href={APP_DASHBOARD_URL}>
                  Show Me What to Build Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              
              <DemoVideoModal
                trigger={
                  <Button
                    variant="outline"
                    size="lg"
                    data-testid="hero-watch-demo-button"
                  >
                    <PlayCircle className="mr-2 h-4 w-4" />
                    Watch 60-sec demo
                  </Button>
                }
              />

              <Button 
                variant="ghost"
                size="lg"
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-secondary-cta-button"
              >
                See How It Works
              </Button>
            </div>
            
            {/* Trust microproof */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-muted-foreground mb-6">
              <span>Demo data</span>
              <span className="text-border">•</span>
              <span>No signup</span>
              <span className="text-border">•</span>
              <span>First decision in 30s</span>
            </div>
            
            {/* Supporting explainer */}
            <p className="text-base text-muted-foreground max-w-xl mx-auto">
              AI analyzes your feedback and tells you what to ship next.
            </p>
          </motion.div>
          
          {/* Hero mock visual */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-16"
          >
            <HeroMockLanes />
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="section bg-muted/50" data-testid="problem-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">Most founders build the wrong things</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              You're not alone. Here's why product decisions are hard.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Guessing what to build",
                description: "You ship features that don't move revenue.",
                icon: Target
              },
              {
                title: "Missing churn signals",
                description: "You miss churn signals until it's too late.",
                icon: AlertCircle
              },
              {
                title: "Drowning in feedback",
                description: "You drown in feedback with no prioritization.",
                icon: Filter
              }
            ].map((pain, i) => (
              <Card 
                key={i} 
                className="p-6 card-hover"
                data-testid="problem-pain-card"
              >
                <pain.icon className="h-10 w-10 text-destructive mb-4" />
                <h3 className="text-lg font-semibold mb-2">{pain.title}</h3>
                <p className="text-sm text-muted-foreground">{pain.description}</p>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground">
              Results: <span className="text-destructive font-semibold">wasted time</span>, <span className="text-destructive font-semibold">lost revenue</span>, <span className="text-destructive font-semibold">silent churn</span>
            </p>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="section" data-testid="solution-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-lg mb-6">VibeSprint tells you what actually matters</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Stop guessing. Start shipping what drives revenue and prevents churn.
              </p>
              
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    <Zap className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Analyze user signals</p>
                    <p className="text-sm text-muted-foreground">From multiple channels in real time</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Detect revenue opportunities</p>
                    <p className="text-sm text-muted-foreground">Know what to build for maximum ROI</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="rounded-full bg-primary/10 p-2">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold">Prioritize what matters</p>
                    <p className="text-sm text-muted-foreground">Build, fix, or ignore — clear next action</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="bento-grid">
              <Card className="p-6 lane-build">
                <div className="text-4xl mb-3">🚀</div>
                <h3 className="font-semibold mb-2">Build Next</h3>
                <p className="text-sm text-muted-foreground">Highest ROI bets</p>
              </Card>
              
              <Card className="p-6 lane-fix">
                <div className="text-4xl mb-3">⚠️</div>
                <h3 className="font-semibold mb-2">Fix Now</h3>
                <p className="text-sm text-muted-foreground">Churn risks</p>
              </Card>
              
              <Card className="p-6 lane-ignore bento-item-large">
                <div className="text-4xl mb-3">❌</div>
                <h3 className="font-semibold mb-2">Ignore</h3>
                <p className="text-sm text-muted-foreground">Noise filtered automatically</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="section bg-muted/50" data-testid="how-it-works-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">How it works</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to better product decisions
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: "1",
                title: "Connect signals",
                description: "Connect Reddit, Hacker News, or RSS — new items land in the same lanes."
              },
              {
                step: "2",
                title: "AI analyzes",
                description: "Score each signal for revenue impact and churn risk with semantic understanding"
              },
              {
                step: "3",
                title: "Ship faster",
                description: "Get clear Build/Fix/Ignore decisions with reasoning and next steps"
              }
            ].map((step, i) => (
              <div key={i} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground font-bold text-lg mb-4">
                  {step.step}
                </div>
                <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section" data-testid="features-section">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-lg mb-4">Everything you need to ship the right things</h2>
          </div>
          
          <div className="bento-grid max-w-5xl mx-auto">
            <Card className="p-6 card-hover">
              <Target className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Outcome-driven prioritization</h3>
              <p className="text-sm text-muted-foreground">Focus on revenue and retention, not vanity metrics</p>
            </Card>
            
            <Card className="p-6 card-hover">
              <TrendingUp className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Revenue opportunity detection</h3>
              <p className="text-sm text-muted-foreground">Spot high-value feature requests before competitors</p>
            </Card>
            
            <Card className="p-6 card-hover">
              <Shield className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Early churn detection</h3>
              <p className="text-sm text-muted-foreground">Catch churn signals before users cancel</p>
            </Card>
            
            <Card className="p-6 card-hover">
              <Filter className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Intelligent noise filtering</h3>
              <p className="text-sm text-muted-foreground">Auto-ignore low-impact requests so you focus on what matters</p>
            </Card>
            
            <Card className="p-6 card-hover">
              <Bell className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Smart alerts</h3>
              <p className="text-sm text-muted-foreground">Get notified of opportunities and risks in real time</p>
            </Card>
            
            <Card className="p-6 card-hover bento-item-large">
              <Zap className="h-8 w-8 text-primary mb-4" />
              <h3 className="font-semibold mb-2">Explainable decisions</h3>
              <p className="text-sm text-muted-foreground">
                Every recommendation comes with clear reasoning, next steps, and expected outcomes
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="section bg-muted/50" data-testid="pricing-section">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="heading-lg mb-4">Simple, transparent pricing</h2>
            <p className="text-muted-foreground mb-8">Start free, upgrade when you're ready</p>

            {/* Monthly / Annual toggle */}
            <div
              className="inline-flex items-center gap-1 p-1.5 bg-card border rounded-full shadow-sm"
              role="tablist"
              aria-label="Billing period"
              data-testid="landing-billing-toggle"
            >
              <button
                type="button"
                role="tab"
                aria-selected={!annualBilling}
                onClick={() => setAnnualBilling(false)}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                  !annualBilling
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="landing-toggle-monthly"
              >
                Monthly
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={annualBilling}
                onClick={() => setAnnualBilling(true)}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                  annualBilling
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid="landing-toggle-annual"
              >
                Annual
                <Badge
                  variant="secondary"
                  className={`text-[10px] px-1.5 py-0 leading-none ${
                    annualBilling ? 'bg-background text-foreground' : ''
                  }`}
                >
                  Save 20%
                </Badge>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {pricingPlans.map((plan) => {
              const price = annualBilling ? plan.annual : plan.monthly;
              const cardClass = plan.highlight
                ? 'p-6 flex flex-col border-2 border-primary ring-2 ring-primary/20 shadow-lg relative'
                : 'p-6 flex flex-col';
              return (
                <Card
                  key={plan.id}
                  className={cardClass}
                  data-testid={`landing-plan-${plan.id}`}
                >
                  {plan.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground">Most popular</Badge>
                    </div>
                  )}
                  <div className="mb-4">
                    <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                    <p className="text-muted-foreground text-xs">{plan.description}</p>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-baseline gap-1">
                      <span
                        className="text-4xl font-bold tabular-nums"
                        data-testid={`landing-price-${plan.id}`}
                      >
                        ${price}
                      </span>
                      <span className="text-muted-foreground text-sm">/mo</span>
                    </div>
                    <p
                      className="text-xs text-muted-foreground mt-1"
                      data-testid={`landing-price-caption-${plan.id}`}
                    >
                      {annualBilling
                        ? `Billed annually — $${plan.annualTotal}/year`
                        : 'Billed monthly'}
                    </p>
                  </div>
                  <Separator className="my-3" />
                  <ul className="space-y-2 mb-6 flex-1 text-sm">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <div className="rounded-full bg-primary/10 p-0.5 mt-1">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        </div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant={plan.variant}
                    className={plan.highlight ? 'w-full btn-cta' : 'w-full'}
                    onClick={() => navigate('/pricing')}
                    data-testid={`landing-pricing-${plan.id}-cta`}
                  >
                    {plan.cta}
                  </Button>
                </Card>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <Button
              asChild
              className="btn-cta"
              data-testid="landing-pricing-get-started"
            >
              <a href={APP_ORIGIN}>Get started free</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="full" />
    </div>
  );
};

export default LandingPage;
